// src/utils/time.js
// Readable dates and times. The backend sends UTC ISO timestamps ("2026-10-03T10:32:00+00:00") and plain
// dates ("2026-10-03"); people see them in their own time zone and language.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n) => String(n).padStart(2, '0');
const clock = (date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// "3 Oct 2026" from "2026-10-03" (a calendar date: no time zone involved).
export function formatDate(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-').map(Number);
    if (!year || !month || !day) return '';
    return `${day} ${MONTHS[month - 1]} ${year}`;
}

// The local calendar day of a timestamp, e.g. "3 Oct 2026".
export function formatDay(iso) {
    const then = new Date(iso);
    if (!iso || Number.isNaN(then.getTime())) return '';
    return `${then.getDate()} ${MONTHS[then.getMonth()]} ${then.getFullYear()}`;
}

// "3 Oct 2026", "3 – 4 Oct 2026", "30 Sep – 2 Oct 2026" or "" when there is no start date.
export function formatDateRange(start, end) {
    if (!start) return end ? formatDate(end) : '';
    if (!end || end === start) return formatDate(start);
    const [sy, sm, sd] = start.split('-').map(Number);
    const [ey, em, ed] = end.split('-').map(Number);
    if (sy === ey && sm === em) return `${sd} – ${ed} ${MONTHS[em - 1]} ${ey}`;
    if (sy === ey) return `${sd} ${MONTHS[sm - 1]} – ${ed} ${MONTHS[em - 1]} ${ey}`;
    return `${formatDate(start)} – ${formatDate(end)}`;
}

// "just now", "5 min ago", "today 14:32", "yesterday 09:05", "3 Oct 14:32" or "3 Oct 2025 14:32".
// `now` is a parameter so this can be tested.
export function formatWhen(iso, now = new Date()) {
    if (!iso) return '';
    const then = new Date(iso);
    if (Number.isNaN(then.getTime())) return '';
    const minutes = Math.floor((now - then) / 60000);
    if (minutes < 1) return 'just now'; // also covers a clock that is slightly off
    if (minutes < 60) return `${minutes} min ago`;
    if (sameDay(then, now)) return `today ${clock(then)}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (sameDay(then, yesterday)) return `yesterday ${clock(then)}`;
    const day = `${then.getDate()} ${MONTHS[then.getMonth()]}`;
    return then.getFullYear() === now.getFullYear() ? `${day} ${clock(then)}` : `${day} ${then.getFullYear()} ${clock(then)}`;
}

// The full local date and time, for tooltips.
export function formatExact(iso) {
    const then = new Date(iso);
    if (!iso || Number.isNaN(then.getTime())) return '';
    return `${then.getDate()} ${MONTHS[then.getMonth()]} ${then.getFullYear()}, ${clock(then)}`;
}
